export interface ParticipantCandidate {
  id: string;
  name: string;
  email: string;
}

/**
 * A participant as stored against an assessment. `name` and `email` are
 * snapshots taken when the assessment was recorded, and `userId` is null once
 * the underlying account has been removed -- the audit record outlives the
 * directory entry.
 */
export interface AssessmentParticipant {
  userId: string | null;
  userName: string;
  userEmail: string;
}

/**
 * Candidates offered in the participant picker: users matching `query` that
 * have not already been chosen.
 *
 * Already-selected users are excluded rather than shown as disabled, because
 * the selection is rendered as chips directly above the list and repeating
 * them reads as though they could be added twice.
 */
export function filterParticipantCandidates(
  users: ParticipantCandidate[],
  query: string,
  selectedIds: string[],
  limit = 10,
): ParticipantCandidate[] {
  const taken = new Set(selectedIds);
  const needle = query.trim().toLowerCase();

  return users
    .filter((u) => !taken.has(u.id))
    .filter(
      (u) =>
        needle === "" ||
        u.name.toLowerCase().includes(needle) ||
        u.email.toLowerCase().includes(needle),
    )
    .slice(0, limit);
}

/**
 * Turns the participants stored on an assessment into the id list the picker
 * works with.
 *
 * Participants whose account has since been deleted have a null userId. They
 * are dropped here because there is nothing left to select, but they are still
 * displayed from the stored snapshot, so re-saving an assessment silently drops
 * them from the record. That is a known limitation, not an oversight.
 */
export function participantIdsFrom(
  participants: AssessmentParticipant[] | null | undefined,
): string[] {
  if (!participants) return [];
  return participants
    .map((p) => p.userId)
    .filter((id): id is string => typeof id === "string" && id !== "");
}

/** Display label for one participant, falling back to the email alone. */
export function participantLabel(p: AssessmentParticipant): string {
  if (p.userName && p.userEmail) return `${p.userName} (${p.userEmail})`;
  return p.userName || p.userEmail || "Unknown participant";
}
